import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, HostListener, Injector, Input, Output, afterNextRender } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyConfig, NgxCurrencyDirective } from 'ngx-currency';

import { environment } from '../../../../../../../environments/environment';
import { EmptyStateComponent } from '../../../../../../shared/components/empty-state/empty-state.component';
import { SelectPesquisavelComponent } from '../../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { AbntPipe } from '../../../../../../shared/pipes/abnt.pipe';
import { AlertService, AlertType } from '../../../../../../shared/services/alert.service';
import { ProdutoListStruct } from '../../../../../../shared/services/structs/produto/produto-list.struct';
import { ProdutoListaPrecoVigenteStruct } from '../../../../../../shared/services/structs/produto/produto-lista-preco-vigente.struct';
import { descontoEfetivoDoItem } from '../../vd-totais/venda-totais.calculo';
import { AbaItens } from '../vd-itens.component';
import { localizacaoEstoqueParaConsultaDeSaldo, validarSaldoNoLancamento } from '../venda-item.estoque';
import { ContextoDePreco, aplicarListaNoItem, preencherDoProduto } from '../venda-item.form';
import { aplicarPrecoDoKit, aplicarQuantidadeDeKits, totalDoKit } from '../venda-item.kit';
import { RegraDoMultiplo, regraDoMultiplo, unidadesDoMultiplo } from '../venda-item.multiplo';
import { OrigemPreco, origemDaLinha, textoOrigemDoPreco, textoVigencia } from '../venda-item.preco';
import { totalIbsCbsDoItem } from '../venda-item.calculo';

/** Um item avulso na lista. */
interface BlocoItem {
    tipo: 'item';

    /**
     * A PRÓPRIA linha, para o `track` do template: rastrear pelo índice recriava as linhas
     * seguintes a cada exclusão ou arrasto.
     */
    chave: FormGroup;
    linha: FormGroup;
    indice: number;
}

/** Uma instância de kit: o cabeçalho derivado + as linhas dos componentes (§6.9). */
interface BlocoKit {
    tipo: 'kit';
    chave: string;
    numeroKit: number;
    linhas: FormGroup[];
    indices: number[];
}

type Bloco = BlocoItem | BlocoKit;

/** Quantidade e preço de uma linha já lançada ao entrar em edição, como estavam no form. */
interface ValoresSalvos {
    linha: FormGroup;
    quantidade: number | null;
    preco: number | null;
}

/** Os controles do cabeçalho de UM kit: Qtde de kits e Preço do kit. */
interface ControlesDoKit {
    quantidade: FormControl<number | null>;
    preco: FormControl<number | null>;
}

/**
 * A lista de itens (§6.2), em `<table>`.
 *
 * O KIT não é uma linha do FormArray: no banco existem só os componentes, marcados com
 * `numeroKit`. O cabeçalho do kit é derivado daqui, então o que está no form é exatamente o que
 * vai para `venda_item`.
 *
 * Cada BLOCO (item avulso ou kit inteiro) é um `<tbody>` arrastável: o kit se move inteiro, e os
 * componentes trocam de lugar só dentro dele (o `<tbody>` do kit é também a drop list deles). No
 * modo card vale o mesmo, com o card do item e o grupo do kit no lugar dos `<tbody>`.
 *
 * As linhas ficam em TEXTO; só a linha em edição (`linhaEmEdicao`, estado do dono do FormArray)
 * mostra campos. A linha nova edita produto, quantidade e preço; a já lançada, quantidade e
 * preço, e só o Salvar confirma o que mudou (sair da linha devolve os valores salvos); o resto é
 * no modal do item.
 */
@Component({
    selector: 'app-vd-itens-lista',
    standalone: true,
    imports: [
        AbntPipe,
        DragDropModule,
        NgTemplateOutlet,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatMenuModule,
        MatTooltipModule,
        NgxCurrencyDirective,
        EmptyStateComponent,
        SelectPesquisavelComponent,
    ],
    // O rótulo das opções do popover de lista é montado em TypeScript e precisa sair com a mesma
    // formatação das células.
    providers: [AbntPipe],
    templateUrl: './vd-itens-lista.component.html',
    styleUrl: './vd-itens-lista.component.css',
})
export class VdItensListaComponent {
    @Input({ required: true }) public itens!: FormArray;
    @Input() public aba: AbaItens = 'itens';
    @Input() public calcularImpostos: boolean = false;
    @Input() public isSomenteLeitura: boolean = false;

    /** Teto de desconto do perfil (§6.5); `null` = sem teto. */
    @Input() public tetoDesconto: number | null = null;

    /** Para resolver o preço ao escolher o produto e ao trocar a lista no popover. */
    @Input() public idListaCabecalho: number | null = null;
    @Input() public priorizarMenorPreco: boolean = true;
    @Input() public listasPorProduto: Map<number, ProdutoListaPrecoVigenteStruct[]> = new Map();

    /** "Permite estoque negativo" (Configurações ▸ Vendas ▸ Gerais), para a conferência ao lançar (§6.2.1). */
    @Input() public set tipoValidacaoEstoqueNegativo(valor: number | null) {
        this.tipoValidacaoAtual = valor;
        this.filtroProduto = this.montarFiltroProduto();
    }

    public get tipoValidacaoEstoqueNegativo(): number | null {
        return this.tipoValidacaoAtual;
    }

    private tipoValidacaoAtual: number | null = null;

    /** Documento já gravado: a reserva dele volta ao saldo consultado no lançamento (T21). */
    @Input() public set idVenda(valor: number | null) {
        this.idVendaAtual = valor;
        this.filtroProduto = this.montarFiltroProduto();
    }

    private idVendaAtual: number | null = null;

    /**
     * A linha que está em edição (estado do dono do FormArray). Só ela mostra campos; a nova
     * também mostra o produto e as ações Salvar e Descartar.
     */
    @Input() public set linhaEmEdicao(valor: FormGroup | null) {
        // Saiu da linha por outro caminho (Adicionar item, busca avançada, exclusão) sem o
        // Salvar: volta o que estava salvo. Depois do ciclo atual, porque o setter roda dentro da
        // detecção de mudanças e a restauração refaz total, comissão e rateio.
        const pendente = this.valoresSalvos;

        if (pendente && pendente.linha !== valor) {
            this.valoresSalvos = null;
            Promise.resolve().then(() => this.restaurar(pendente));
        }

        this.linhaEmEdicaoAtual = valor;
        this.filtroProduto = this.montarFiltroProduto();
    }

    public get linhaEmEdicao(): FormGroup | null {
        return this.linhaEmEdicaoAtual;
    }

    private linhaEmEdicaoAtual: FormGroup | null = null;

    /** A linha criada por "Adicionar item" e ainda não salva: Descartar a remove. */
    @Input() public set linhaNova(valor: FormGroup | null) {
        if (valor !== this.linhaNovaAtual) this.opcaoDoProduto = [];

        this.linhaNovaAtual = valor;
    }

    public get linhaNova(): FormGroup | null {
        return this.linhaNovaAtual;
    }

    private linhaNovaAtual: FormGroup | null = null;

    @Input() public mascaraPreco!: Partial<NgxCurrencyConfig>;
    @Input() public mascaraQuantidade!: Partial<NgxCurrencyConfig>;
    @Input() public mascaraInteira!: Partial<NgxCurrencyConfig>;

    /** Abre o modal do item. */
    @Output() public editar = new EventEmitter<number>();
    @Output() public excluir = new EventEmitter<number>();
    @Output() public detalhesEstoque = new EventEmitter<number>();

    /** Clique numa linha em texto: ela passa a ser a linha em edição. */
    @Output() public editarNaLinha = new EventEmitter<FormGroup>();

    /** Clique fora da linha em edição: o dono a devolve para texto, se ela for válida. */
    @Output() public sairDaEdicao = new EventEmitter<void>();

    /** Salvar e Descartar da linha nova. */
    @Output() public salvarLinha = new EventEmitter<void>();
    @Output() public descartarLinha = new EventEmitter<void>();

    /** O struct que a API devolveu, para o dono do FormArray guardar as candidatas de preço. */
    @Output() public produtoEscolhido = new EventEmitter<ProdutoListStruct>();

    /** Produto-kit escolhido na linha `indice`: quem abre o diálogo é a seção. */
    @Output() public kitEscolhido = new EventEmitter<{ indice: number; produto: ProdutoListStruct }>();

    /** Produto com múltiplo escolhido (§6.2.3): a seção abre o diálogo. */
    @Output() public multiploEscolhido = new EventEmitter<{ indice: number; produto: ProdutoListStruct }>();

    /** Qtde de item com múltiplo clicada: reabrir o diálogo para a linha (§6.2.3). */
    @Output() public editarMultiplo = new EventEmitter<number>();

    /** Excluir o kit inteiro; a seção confirma e remove as linhas. */
    @Output() public excluirKit = new EventEmitter<number>();

    /** Reordenação pela alça: a seção renumera a sequência. */
    @Output() public reordenou = new EventEmitter<void>();

    /** Aviso para a seção mostrar: kit fixo que não fechou no preço. */
    @Output() public aviso = new EventEmitter<string>();

    /** O produto é buscado ONLINE: a lista inteira não cabe em memória. */
    public readonly urlBuscaProduto = `${environment.urlApiProduto}Lookup/listProdutoSelect`;

    /**
     * Parâmetros da busca do produto da linha nova. A REFERÊNCIA é estável (o select reconsulta
     * quando o objeto troca): é refeita só quando a linha em edição ou a regra de estoque mudam.
     * O depósito entra na regra "depósito selecionado", e é o do ITEM.
     */
    public filtroProduto: Record<string, string | number | boolean> = this.montarFiltroProduto();

    /** O produto escolhido na linha nova, como opção conhecida do select (referência estável). */
    public opcaoDoProduto: ProdutoListStruct[] = [];

    /** Kits recolhidos (estado de UI, dura a sessão). */
    private recolhidos = new Set<number>();

    /** Controles do cabeçalho de cada kit, criados sob demanda e ligados às linhas. */
    private controlesPorKit = new Map<number, ControlesDoKit>();

    /**
     * O clique que colocou uma linha em edição também chega ao documento; sem isto, o documento
     * o trataria como "clique fora" da linha anterior e devolveria a nova para texto.
     */
    private cliqueJaTratado: boolean = false;

    /**
     * Quantidade e preço da linha já lançada no momento em que ela entrou em edição. Enquanto o que
     * está nos campos difere disto, a linha mostra Salvar e Descartar; só o Salvar confirma.
     */
    private valoresSalvos: ValoresSalvos | null = null;

    constructor(
        private abnt: AbntPipe,
        private alertService: AlertService,
        private injector: Injector,
        private changeDetector: ChangeDetectorRef,
    ) {}

    private montarFiltroProduto(): Record<string, string | number | boolean> {
        const deposito = localizacaoEstoqueParaConsultaDeSaldo(
            this.tipoValidacaoAtual,
            this.linhaEmEdicaoAtual?.get('dados.idLocalizacaoEstoque')?.value ?? null,
        );

        return {
            apenasVendaveis: true,
            incluirListasPreco: true,
            incluirComposicaoKit: true,
            incluirSaldo: true,
            ...(deposito ? { idLocalizacaoEstoque: deposito } : {}),
            ...(this.idVendaAtual ? { idVendaReserva: this.idVendaAtual } : {}),
        };
    }

    public get linhas(): FormGroup[] {
        return this.itens.controls as FormGroup[];
    }

    /**
     * A lista como blocos: item avulso, ou kit com todos os seus componentes. Componentes de uma
     * mesma instância entram no bloco onde o PRIMEIRO aparece. Recalculado a cada ciclo; é barato
     * e evita estado duplicado.
     */
    public get blocos(): Bloco[] {
        const blocos: Bloco[] = [];
        const kitPorNumero = new Map<number, BlocoKit>();

        this.linhas.forEach((linha, indice) => {
            const numeroKit = linha.get('kit.numeroKit')?.value;

            if (numeroKit == null) {
                blocos.push({ tipo: 'item', chave: linha, linha, indice });
                return;
            }

            let bloco = kitPorNumero.get(Number(numeroKit));

            if (!bloco) {
                bloco = { tipo: 'kit', chave: `k${numeroKit}`, numeroKit: Number(numeroKit), linhas: [], indices: [] };
                kitPorNumero.set(bloco.numeroKit, bloco);
                blocos.push(bloco);
            }

            bloco.linhas.push(linha);
            bloco.indices.push(indice);
        });

        // Kit excluído leva os controles do cabeçalho junto, senão o mapa só cresce.
        for (const numeroKit of [...this.controlesPorKit.keys()]) {
            if (!kitPorNumero.has(numeroKit)) this.controlesPorKit.delete(numeroKit);
        }

        return blocos;
    }

    // ==================================================================== edição na linha

    public estaEmEdicao(linha: FormGroup): boolean {
        return !this.isSomenteLeitura && linha === this.linhaEmEdicao;
    }

    public ehLinhaNova(linha: FormGroup): boolean {
        return linha === this.linhaNova;
    }

    public kitEmEdicao(bloco: BlocoKit): boolean {
        return !this.isSomenteLeitura && !!this.linhaEmEdicao && bloco.linhas.includes(this.linhaEmEdicao);
    }

    /** Onde o clique não põe a linha em edição: ações e alça, na tabela (por coluna) e no card. */
    private readonly seletorForaDaEdicao = '.col-acoes, .col-numero, .vil-acoes, .vil-alca';

    /**
     * Clique numa linha em texto a coloca em edição. Só na aba de produtos, onde estão os campos;
     * componente de kit não edita na linha (quantidade e preço são do kit). Clique na alça ou nas
     * ações não conta: cada um tem a sua função. Vale para a linha da tabela e para o card.
     */
    public aoClicarNaLinha(evento: MouseEvent, linha: FormGroup): void {
        if (this.aba !== 'itens' || linha === this.linhaEmEdicao) return;

        if (this.cliqueDentroDe(evento, this.seletorForaDaEdicao)) return;

        // Em leitura não há edição na linha: o clique abre o modal do item, todo em leitura (PO, 23/10).
        if (this.isSomenteLeitura) {
            this.editar.emit(this.indiceDe(linha));
            return;
        }

        this.restaurarPendente();
        this.valoresSalvos = {
            linha,
            quantidade: linha.get('dados.quantidade')?.value ?? null,
            preco: linha.get('dados.precoLiquidoUnitario')?.value ?? null,
        };

        this.cliqueJaTratado = true;
        this.editarNaLinha.emit(linha);
        this.focarCampoAoEditar(evento);
    }

    /** Clique no cabeçalho do kit: Qtde de kits e Preço do kit passam a campos. */
    public aoClicarNoKit(evento: MouseEvent, bloco: BlocoKit): void {
        if (this.aba !== 'itens' || this.kitEmEdicao(bloco)) return;

        if (this.cliqueDentroDe(evento, this.seletorForaDaEdicao)) return;

        // Em leitura o cabeçalho do kit abre o modal do primeiro componente, em leitura.
        if (this.isSomenteLeitura) {
            this.editar.emit(this.indiceDe(bloco.linhas[0]));
            return;
        }

        this.editarKit(bloco);
        this.focarCampoAoEditar(evento);
    }

    public editarKit(bloco: BlocoKit): void {
        this.restaurarPendente();
        this.cliqueJaTratado = true;
        this.editarNaLinha.emit(bloco.linhas[0]);
    }

    /**
     * O clique troca o texto pelos campos, mas o foco ficava no documento: o que se digitava não
     * chegava a campo nenhum. Depois de renderizar, o foco vai para o campo da coluna clicada (preço,
     * ou a quantidade nas demais), com o conteúdo selecionado para a digitação substituí-lo. Os campos
     * são procurados no elemento clicado (a linha ou o card): a tabela e os cards coexistem no DOM,
     * e o que não está visível não recebe foco.
     */
    private focarCampoAoEditar(evento: MouseEvent): void {
        const clicado = evento.currentTarget as HTMLElement | null;
        const seletor = this.cliqueDentroDe(evento, '.col-preco, .vil-cartao-preco')
            ? '.col-preco input:not([readonly]), .vil-cartao-preco input:not([readonly])'
            : '.col-qtde input:not([readonly]), .vil-cartao-qtde input:not([readonly])';

        afterNextRender(() => {
            const campo = clicado?.querySelector<HTMLInputElement>(seletor)
                ?? clicado?.querySelector<HTMLInputElement>('td.num input:not([readonly]), .vil-cartao-valores input:not([readonly])');

            campo?.focus();
            campo?.select();
        }, { injector: this.injector });
    }

    /** Linha já lançada com quantidade ou preço diferente do que estava ao entrar em edição. */
    public temAlteracaoPendente(linha: FormGroup): boolean {
        const salvos = this.valoresSalvos;

        return !!salvos && salvos.linha === linha && this.diferenteDoSalvo(salvos);
    }

    private diferenteDoSalvo(salvos: ValoresSalvos): boolean {
        return this.valor(salvos.linha, 'dados.quantidade') !== (Number(salvos.quantidade ?? 0) || 0)
            || this.valor(salvos.linha, 'dados.precoLiquidoUnitario') !== (Number(salvos.preco ?? 0) || 0);
    }

    /**
     * Editar item (modal): a linha sai da edição antes, porque o "Confirmar" do modal já aplica
     * quantidade e preço e não pode virar alteração pendente da linha.
     */
    public abrirItem(linha: FormGroup): void {
        if (linha === this.linhaEmEdicao) {
            this.valoresSalvos = null;
            this.sairDaEdicao.emit();
        }

        this.editar.emit(this.indiceDe(linha));
    }

    /** Salvar: o que está nos campos passa a ser o valor da linha, e ela volta para texto. */
    public salvarAlteracao(): void {
        this.valoresSalvos = null;
        this.salvarLinha.emit();
    }

    /** Descartar: quantidade e preço voltam ao que estava salvo, e a linha volta para texto. */
    public descartarAlteracao(): void {
        this.restaurarPendente();
        this.sairDaEdicao.emit();
    }

    /** Sair da linha sem o Salvar não confirma nada: devolve quantidade e preço salvos. */
    private restaurarPendente(): void {
        const salvos = this.valoresSalvos;

        this.valoresSalvos = null;

        if (salvos) this.restaurar(salvos);
    }

    private restaurar(salvos: ValoresSalvos): void {
        if (!this.diferenteDoSalvo(salvos)) return;

        salvos.linha.get('dados.quantidade')?.setValue(salvos.quantidade);
        salvos.linha.get('dados.precoLiquidoUnitario')?.setValue(salvos.preco);
    }

    /**
     * Clique fora da linha em edição a devolve para texto. Não conta o que acontece no overlay do
     * CDK (lista do select, diálogos, popover): é a própria edição em andamento.
     */
    @HostListener('document:click', ['$event'])
    public aoClicarNoDocumento(evento: MouseEvent): void {
        if (this.cliqueJaTratado) {
            this.cliqueJaTratado = false;
            return;
        }

        if (!this.linhaEmEdicao) return;

        if (this.cliqueDentroDe(evento, '.cdk-overlay-container, .linha-em-edicao')) return;

        this.restaurarPendente();
        this.sairDaEdicao.emit();
    }

    /**
     * Confere os ancestrais do alvo NO MOMENTO do clique (`composedPath`): o elemento clicado pode
     * sair do DOM antes de o evento chegar aqui (a opção do select, o Salvar da linha nova), e aí
     * `closest` não acharia mais a célula nem o overlay.
     */
    private cliqueDentroDe(evento: Event, seletor: string): boolean {
        return evento.composedPath().some((no) => no instanceof Element && no.matches(seletor));
    }

    /**
     * Escolher o produto na linha nova preenche a linha com o que vem do CADASTRO e resolve o
     * preço. Kit e múltiplo seguem para os diálogos da seção.
     */
    public aoEscolherProduto(linha: FormGroup, produto: ProdutoListStruct | null): void {
        if (!produto?.idProduto) return;

        const indice = this.indiceDe(linha);

        if (produto.isKit) {
            this.kitEscolhido.emit({ indice, produto });
            return;
        }

        const regra = regraDoMultiplo(produto);

        // Saldo no lançamento (§6.2.1): a regra da empresa decide entre avisar e recusar. Com
        // múltiplo a quantidade sai do diálogo, e a seção confere o saldo lá.
        if (!regra) {
            const saldo = validarSaldoNoLancamento(this.tipoValidacaoAtual, produto.saldoDisponivel, 1,
                produto.descricao, this.casasDecimaisQuantidade);

            if (!saldo.permitido) {
                this.alertService.show('Estoque insuficiente', saldo.aviso ?? '', AlertType.warning);
                linha.get('dados.idProduto')?.setValue(null, { emitEvent: false });
                return;
            }

            if (saldo.aviso) this.alertService.show('Saldo insuficiente', saldo.aviso, AlertType.info);
        }

        this.produtoEscolhido.emit(produto);

        preencherDoProduto(linha, produto, this.contextoDePreco);

        this.opcaoDoProduto = [produto];

        if (regra) this.multiploEscolhido.emit({ indice, produto });
    }

    public produtoControl(linha: FormGroup): FormControl {
        return linha.get('dados.idProduto') as FormControl;
    }

    public controle(linha: FormGroup, caminho: string): FormControl {
        return linha.get(caminho) as FormControl;
    }

    public temProduto(linha: FormGroup): boolean {
        return !!linha.get('dados.idProduto')?.value;
    }

    // ==================================================================== leitura das linhas

    public indiceDe(linha: FormGroup): number {
        return this.itens.controls.indexOf(linha);
    }

    public valor(linha: FormGroup, caminho: string): number {
        return Number(linha.get(caminho)?.value ?? 0) || 0;
    }

    /** IBS + CBS do item: o grupo não grava o total. */
    public totalIbsCbs(linha: FormGroup): number {
        return totalIbsCbsDoItem(linha);
    }

    /** "SKU 123 · EAN 789", a identificação abaixo da descrição. */
    public identificacao(linha: FormGroup): string {
        const sku = linha.get('dados.codigoSku')?.value;
        const ean = linha.get('dados.gtinEan')?.value;

        return [sku ? `SKU ${sku}` : null, ean ? `EAN ${ean}` : null].filter(Boolean).join(' · ');
    }

    /** Componente de kit: quantidade e preço são do kit (§6.9). */
    public ehComponenteDeKit(linha: FormGroup): boolean {
        return linha.get('kit.numeroKit')?.value != null;
    }

    /** Regra de múltiplo do produto da linha. Componente de kit não tem (§6.2.3). */
    public regraMultiplo(linha: FormGroup): RegraDoMultiplo | null {
        if (this.ehComponenteDeKit(linha)) return null;

        return regraDoMultiplo({
            isControlaMultiplo: linha.get('multiplo.isControlaMultiplo')?.value,
            fatorMultiplo: linha.get('multiplo.fatorMultiplo')?.value,
            unidadeMultiplo: linha.get('multiplo.unidadeMultiplo')?.value,
        });
    }

    /** "6 CX": a equivalência em unidades de múltiplo, apoio da Qtde (§6.2.3). */
    public equivalenciaDoMultiplo(linha: FormGroup): string {
        const regra = this.regraMultiplo(linha);

        if (!regra) return '';

        const unidades = unidadesDoMultiplo(this.valor(linha, 'dados.quantidade'), regra.fatorMultiplo);

        return `${this.abnt.transform(unidades, 'quantidade')} ${regra.unidadeMultiplo}`.trim();
    }

    /** Qtde de item com múltiplo: em leitura, e o clique reabre o diálogo (§6.2.3). */
    public abrirMultiplo(linha: FormGroup): void {
        if (!this.isSomenteLeitura && this.regraMultiplo(linha)) this.editarMultiplo.emit(this.indiceDe(linha));
    }

    /** Unidade que NÃO fraciona aceita inteiro. */
    public mascaraDaQuantidade(linha: FormGroup): Partial<NgxCurrencyConfig> {
        return linha.get('dados.isUnidadeMedidaFracionada')?.value === false ? this.mascaraInteira : this.mascaraQuantidade;
    }

    /** Casas da quantidade em texto: unidade que não fraciona sem decimais; a fracionada nas da empresa. */
    public casasDaQuantidade(linha: FormGroup): number | null {
        return linha.get('dados.isUnidadeMedidaFracionada')?.value === false ? 0 : null;
    }

    public nomeDoKitDaLinha(linha: FormGroup): string {
        return linha.get('kit.descricaoKit')?.value || 'kit';
    }

    // -------------------------------------------------------------------- teto (§6.5)

    /** Desconto efetivo sobre o BRUTO: inclui a redução de preço e a parcela do desconto global (D21). */
    public excedeuOTeto(linha: FormGroup): boolean {
        return this.tetoDesconto != null && descontoEfetivoDoItem(linha) > this.tetoDesconto;
    }

    public avisoDoTeto(linha: FormGroup): string {
        const efetivo = this.abnt.transform(descontoEfetivoDoItem(linha), 'preco', 2);
        const origem = this.valor(linha, 'dados.valorDescontoRateado') > 0 ? ' (inclui o desconto do documento)' : '';

        return `Desconto efetivo de ${efetivo}%${origem} acima do limite de ${this.tetoDesconto}% do seu perfil. `
            + 'O documento vai pedir liberação ao salvar.';
    }

    // -------------------------------------------------------------------- origem do preço (§6.2.2.3)

    /** As listas vigentes do produto da linha. Vazia enquanto não há produto. */
    private candidatasDePreco(linha: FormGroup): ProdutoListaPrecoVigenteStruct[] {
        const idProduto = linha.get('dados.idProduto')?.value;

        return idProduto ? this.listasPorProduto.get(idProduto) ?? [] : [];
    }

    private listaDoPreco(linha: FormGroup): ProdutoListaPrecoVigenteStruct | null {
        const id = linha.get('dados.idListaPreco')?.value;

        return this.candidatasDePreco(linha).find((l) => l.idListaPreco === id) ?? null;
    }

    private origemDoPreco(linha: FormGroup): OrigemPreco {
        return origemDaLinha(this.listaDoPreco(linha), this.idListaCabecalho, !!linha.get('dados.isListaPrecoManual')?.value);
    }

    /** O rastreio só aparece quando o preço NÃO veio da lista esperada; no caso comum seria ruído. */
    public mostraRastreioDoPreco(linha: FormGroup): boolean {
        const origem = this.origemDoPreco(linha);

        return origem === 'automatica' || origem === 'manual';
    }

    public rastreioDoPreco(linha: FormGroup): string {
        const base = textoVigencia(this.listaDoPreco(linha));

        return linha.get('dados.isListaPrecoManual')?.value ? `${base} (escolha manual)` : base;
    }

    /** O título do botão de lista muda conforme já exista uma definida para o item (§6.2.5). */
    public tituloBotaoLista(linha: FormGroup): string {
        const lista = this.listaDoPreco(linha);

        if (!lista) return 'Definir lista de preço para este item';

        const comoFoi = linha.get('dados.isListaPrecoManual')?.value ? 'escolha manual' : 'aplicada automaticamente';

        return `Lista de preço do item: ${lista.descricao} (${comoFoi})`;
    }

    // ==================================================================== popover "lista de preço" (§6.2.5)

    /** O que está marcado no popover ENQUANTO ele está aberto. Só o Aplicar move a linha. */
    public readonly listaEscolhidaNoPopover = new FormControl<number | null>(null);

    /** As opções do select. CAMPO, remontado ao abrir (getter faria o select reprocessar a cada ciclo). */
    public opcoesDeLista: { id: number; descricao: string }[] = [];

    /** A frase acima do select, dizendo de onde veio o preço que está na linha agora. */
    public origemDoPrecoAtual: string = '';

    /** A linha e o gatilho do popover aberto: o painel tem `stopPropagation`, então o fecho é explícito. */
    private linhaDoPopover: FormGroup | null = null;
    private gatilhoDoPopover: MatMenuTrigger | null = null;

    public aoAbrirPopoverDeLista(linha: FormGroup, gatilho: MatMenuTrigger): void {
        this.linhaDoPopover = linha;
        this.gatilhoDoPopover = gatilho;

        this.opcoesDeLista = this.candidatasDePreco(linha).map((l) => ({
            id: l.idListaPreco,
            descricao: `${l.descricao} - ${this.abnt.transform(l.preco, 'preco')}`,
        }));

        const ehManual = !!linha.get('dados.isListaPrecoManual')?.value;

        // Só item com lista FIXADA à mão nasce com algo marcado; o resto abre em "Seguir a regra automática".
        this.listaEscolhidaNoPopover.setValue(ehManual ? linha.get('dados.idListaPreco')?.value ?? null : null);

        this.origemDoPrecoAtual = textoOrigemDoPreco(this.origemDoPreco(linha), this.listaDoPreco(linha)?.descricao ?? null);
    }

    public fecharPopoverDeLista(): void {
        this.gatilhoDoPopover?.closeMenu();
    }

    /** Nulo = "Seguir a regra automática": `aplicarListaNoItem` re-resolve pelo cabeçalho. */
    public aplicarListaDoPopover(): void {
        const linha = this.linhaDoPopover;

        if (!linha) return;

        const escolhido = this.listaEscolhidaNoPopover.value;
        const candidatas = this.candidatasDePreco(linha);
        const lista = escolhido ? candidatas.find((l) => l.idListaPreco === escolhido) ?? null : null;

        aplicarListaNoItem(linha, lista, this.contextoDePreco, candidatas);

        // O Aplicar confirma o preço novo: na linha em edição ele passa a ser o valor salvo.
        if (this.valoresSalvos?.linha === linha) {
            this.valoresSalvos = {
                linha,
                quantidade: linha.get('dados.quantidade')?.value ?? null,
                preco: linha.get('dados.precoLiquidoUnitario')?.value ?? null,
            };
        }

        this.fecharPopoverDeLista();
    }

    // ==================================================================== kit: cabeçalho

    public estaRecolhido(numeroKit: number): boolean {
        return this.recolhidos.has(numeroKit);
    }

    public alternarKit(numeroKit: number): void {
        if (this.recolhidos.has(numeroKit)) this.recolhidos.delete(numeroKit);
        else this.recolhidos.add(numeroKit);
    }

    /**
     * Kit recolhido ao apertar a alça e que ainda não começou a ser arrastado: se o arraste não
     * começar (só um clique na alça), ele volta a abrir ao soltar o botão.
     */
    private kitRecolhidoNaAlca: number | null = null;

    /**
     * Apertar a alça do kit o recolhe ANTES de o arraste começar: o CDK mede o elemento no aperto
     * (tamanho do preview) e o clona no início do arraste (preview e placeholder), então recolher
     * no `cdkDragStarted` seria tarde e os clones levariam os componentes. A detecção de mudanças é
     * síncrona para o DOM já estar recolhido quando o evento chega ao CDK, no `cdkDrag` do kit.
     */
    public recolherParaArrastar(bloco: BlocoKit): void {
        if (this.isSomenteLeitura || this.recolhidos.has(bloco.numeroKit)) return;

        this.recolhidos.add(bloco.numeroKit);
        this.kitRecolhidoNaAlca = bloco.numeroKit;
        this.changeDetector.detectChanges();
    }

    /** O arraste começou: o kit fica recolhido, inclusive depois de solto. */
    public aoIniciarArrasteDoKit(): void {
        this.kitRecolhidoNaAlca = null;
    }

    @HostListener('document:mouseup')
    @HostListener('document:touchend')
    public aoSoltarAlcaDoKit(): void {
        if (this.kitRecolhidoNaAlca == null) return;

        this.recolhidos.delete(this.kitRecolhidoNaAlca);
        this.kitRecolhidoNaAlca = null;
    }

    public nomeDoKit(bloco: BlocoKit): string {
        return bloco.linhas[0]?.get('kit.descricaoKit')?.value || 'Kit';
    }

    public skuDoKit(bloco: BlocoKit): string | null {
        return bloco.linhas[0]?.get('kit.codigoSkuKit')?.value ?? null;
    }

    public ehPrecoFixo(bloco: BlocoKit): boolean {
        return bloco.linhas[0]?.get('kit.isPrecoFixoKit')?.value === true;
    }

    public regraDoPrecoDoKit(bloco: BlocoKit): string {
        return this.ehPrecoFixo(bloco)
            ? 'Preço fixo: alterar o preço do kit rateia o valor entre os componentes.'
            : 'Soma dos componentes: o preço do kit é a soma dos preços dos componentes.';
    }

    public quantidadeDeKits(bloco: BlocoKit): number {
        return Number(bloco.linhas[0]?.get('kit.quantidadeKit')?.value ?? 0);
    }

    public precoDoKit(bloco: BlocoKit): number {
        return Number(bloco.linhas[0]?.get('kit.precoKit')?.value ?? 0);
    }

    public totalDoKit(bloco: BlocoKit): number {
        return totalDoKit(bloco.linhas);
    }

    public controlesDoKit(bloco: BlocoKit): ControlesDoKit {
        let controles = this.controlesPorKit.get(bloco.numeroKit);

        if (controles) {
            // Kit recalculado por fora (diálogo, carga do documento): o cabeçalho segue.
            this.sincronizarControles(controles, bloco);
            return controles;
        }

        // `updateOn: 'blur'`: a máscara emite a cada tecla, e "12" aplicaria 1 e depois 12,
        // refazendo as N linhas duas vezes e disparando o aviso de "não fecha" num valor que o
        // usuário nem quis.
        controles = {
            quantidade: new FormControl<number | null>(this.quantidadeDeKits(bloco) || 1, { updateOn: 'blur' }),
            preco: new FormControl<number | null>(this.precoDoKit(bloco), { updateOn: 'blur' }),
        };

        controles.quantidade.valueChanges.subscribe((valor) => {
            const kits = Number(valor ?? 0);

            if (kits <= 0) return;

            if (!aplicarQuantidadeDeKits(this.linhasAtuais(bloco.numeroKit), kits, this.casasDecimaisQuantidade, this.casasDecimaisPreco)) {
                this.aviso.emit('Com essa quantidade a soma dos componentes não fecha exatamente no preço do kit. O documento não será aceito assim.');
            }
        });

        controles.preco.valueChanges.subscribe((valor) => {
            const linhas = this.linhasAtuais(bloco.numeroKit);

            if (!linhas.length || linhas[0].get('kit.isPrecoFixoKit')?.value !== true) return;

            if (!aplicarPrecoDoKit(linhas, Number(valor ?? 0), this.casasDecimaisPreco)) {
                this.aviso.emit('Com esse preço a soma dos componentes não fecha exatamente no preço do kit. O documento não será aceito assim.');
            }
        });

        this.controlesPorKit.set(bloco.numeroKit, controles);

        return controles;
    }

    /**
     * O cabeçalho mostra o que as linhas têm. Só escreve quando diverge, e sem evento, senão o
     * próprio `valueChanges` reaplicaria o valor nas linhas e o campo focado perderia a digitação.
     */
    private sincronizarControles(controles: ControlesDoKit, bloco: BlocoKit): void {
        const quantidade = this.quantidadeDeKits(bloco);
        const preco = this.precoDoKit(bloco);

        // Controle em zero e o usuário limpando o campo para digitar: não devolver o valor antigo.
        if (Number(controles.quantidade.value ?? 0) > 0 && Number(controles.quantidade.value ?? 0) !== quantidade && quantidade > 0) {
            controles.quantidade.setValue(quantidade, { emitEvent: false });
        }

        if (Number(controles.preco.value ?? 0) !== preco) {
            controles.preco.setValue(preco, { emitEvent: false });
        }
    }

    private linhasAtuais(numeroKit: number): FormGroup[] {
        return this.linhas.filter((l) => Number(l.get('kit.numeroKit')?.value) === numeroKit);
    }

    // ==================================================================== arrastar

    /**
     * Reordena por BLOCO, preservando os MESMOS FormGroups (não recria linha, para não perder
     * validators, dirty e touched). Um kit se move inteiro.
     */
    public aoSoltar(evento: CdkDragDrop<unknown>): void {
        if (evento.previousIndex === evento.currentIndex) return;

        const blocos = [...this.blocos];

        moveItemInArray(blocos, evento.previousIndex, evento.currentIndex);

        const controles: AbstractControl[] = [];

        for (const bloco of blocos) {
            if (bloco.tipo === 'item') controles.push(bloco.linha);
            else controles.push(...bloco.linhas);
        }

        this.reescreverOrdem(controles);
    }

    /**
     * Reordena os componentes DENTRO de um kit. A drop list do kit não é conectada à externa, então
     * o CDK já impede que um componente saia dele.
     */
    public aoSoltarComponente(bloco: BlocoKit, evento: CdkDragDrop<unknown>): void {
        if (evento.previousIndex === evento.currentIndex) return;

        const ordenados = [...bloco.linhas];

        moveItemInArray(ordenados, evento.previousIndex, evento.currentIndex);

        const controles: AbstractControl[] = [];

        for (const b of this.blocos) {
            if (b.tipo === 'item') controles.push(b.linha);
            else if (b.numeroKit === bloco.numeroKit) controles.push(...ordenados);
            else controles.push(...b.linhas);
        }

        this.reescreverOrdem(controles);
    }

    private reescreverOrdem(controles: AbstractControl[]): void {
        while (this.itens.length) {
            this.itens.removeAt(0, { emitEvent: false });
        }

        controles.forEach((controle, indice) => this.itens.insert(indice, controle, { emitEvent: false }));

        this.itens.updateValueAndValidity();
        this.reordenou.emit();
    }

    // ==================================================================== contexto

    /** O que a resolução de preço precisa saber do documento, num lugar só. */
    private get contextoDePreco(): ContextoDePreco {
        return {
            idListaCabecalho: this.idListaCabecalho,
            priorizarMenorPreco: this.priorizarMenorPreco,
            casasDecimaisPreco: this.casasDecimaisPreco,
            casasDecimaisQuantidade: this.casasDecimaisQuantidade,
        };
    }

    private get casasDecimaisPreco(): number {
        return this.mascaraPreco?.precision ?? 2;
    }

    private get casasDecimaisQuantidade(): number {
        return this.mascaraQuantidade?.precision ?? 2;
    }
}
